import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PromoCode as IPromoCode } from '../../../store/promoCode/api/interface';
import { useFormik } from 'formik';
import {
  createPromoCode,
  getPromoCode,
  patchPromoCode,
} from '../../../store/promoCode/api';
import { setStatus } from '../../../store/promoCode/promoCodeSlice';
import { useEffect } from 'react';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit } from 'lodash';
// import {
//   setStatus as selectAddOnProductSetStatus,
//   reset as resetSelectAddOnProductReset,
// } from '../../../store/selectAddOnProduct/selectAddOnProductSlice';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import { DISPLAY_TYPE } from '../../../constants';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import promoCodeSchema from '../../../validation/promoCodeSchema';

const PromoCodeForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IPromoCode; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.promoCode);
  // const [response, setResponse] = useState<any>();

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      id: '',
      code: '',
      discountType: '',
      discountAmount: '',
      startDate: new Date(),
      endDate: new Date(),
      usageLimit: '',
      usageLimitPerUser: '1',
      minimumPurchaseAmount: '',
      maximumDiscountAmount: '',
      // appliesTo: '',
      activeState: true,
    },
    validationSchema: promoCodeSchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createPromoCode(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.addon_category;
          await dispatch(patchPromoCode(reqBody)).unwrap();
        }
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        // dispatch(resetSelectAddOnProductReset());
        // dispatch(selectAddOnProductSetStatus({ status: 'idle' }));
        navigation('/promo-code');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        setValues(state);
        // setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getPromoCode({ id: decodeParam })
          ).unwrap();
          setValues(record);
          // setResponse(record);
        } catch (error: any) {
          navigation('/promo-code');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, setValues, state]);

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <Card sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: {
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  },
                }}
              >
                <TextField
                  fullWidth
                  label={'Code'}
                  name='code'
                  id='code'
                  onChange={handleChange}
                  value={values.code}
                  error={Boolean(errors.code && touched.code)}
                  helperText={
                    Boolean(errors.code && touched.code) && errors.code
                  }
                />

                <FormControl
                  fullWidth
                  error={Boolean(touched?.discountType && errors?.discountType)}
                >
                  <InputLabel>{'Type'}</InputLabel>
                  <Select
                    name='discountType'
                    labelId='discountType'
                    id='discountType'
                    value={values.discountType}
                    label={'Type'}
                    onChange={(e) =>
                      setFieldValue('discountType', e.target.value)
                    }
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                      },
                    }}
                  >
                    {DISPLAY_TYPE?.map((mode) => (
                      <MenuItem
                        key={mode.value}
                        value={mode.value}
                      >
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(touched.discountType && errors.discountType) && (
                    <FormHelperText>{errors?.discountType}</FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label={'Discount Amount'}
                  name='discountAmount'
                  id='discountAmount'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.discountAmount}
                  error={Boolean(
                    errors.discountAmount && touched.discountAmount
                  )}
                  helperText={
                    Boolean(errors.discountAmount && touched.discountAmount) &&
                    errors?.discountAmount
                  }
                />

                <TextField
                  fullWidth
                  label={'Usage Limit'}
                  name='usageLimit'
                  id='usageLimit'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.usageLimit}
                  error={Boolean(errors.usageLimit && touched.usageLimit)}
                  helperText={
                    Boolean(errors.usageLimit && touched.usageLimit) &&
                    errors?.usageLimit
                  }
                />

                <TextField
                  fullWidth
                  label={'Usage Limit per User'}
                  name='usageLimitPerUser'
                  id='usageLimitPerUser'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.usageLimitPerUser}
                  error={Boolean(
                    errors.usageLimitPerUser && touched.usageLimitPerUser
                  )}
                  helperText={
                    Boolean(
                      errors.usageLimitPerUser && touched.usageLimitPerUser
                    ) && errors?.usageLimitPerUser
                  }
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={'Start Date'}
                    value={values.startDate ? values.startDate : null}
                    onChange={(newValue: Date | null) => {
                      newValue?.setHours(0, 0, 0, 0);
                      setFieldValue('startDate', newValue);
                    }}
                    inputFormat='dd/MM/yyyy'
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={Boolean(touched.startDate && errors.startDate)}
                        helperText={
                          Boolean(errors.startDate && touched.startDate) &&
                          (errors.startDate as any)
                        }
                        fullWidth
                        name='startDate'
                      />
                    )}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={'End Date'}
                    value={values.endDate ? values.endDate : null}
                    minDate={values.startDate}
                    onChange={(newValue: Date | null) => {
                      newValue?.setHours(23, 59, 59, 999);
                      setFieldValue('endDate', newValue);
                    }}
                    inputFormat='dd/MM/yyyy'
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={Boolean(touched.endDate && errors.endDate)}
                        helperText={
                          Boolean(errors.endDate && touched.endDate) &&
                          (errors.endDate as any)
                        }
                        fullWidth
                        name='endDate'
                      />
                    )}
                  />
                </LocalizationProvider>

                <TextField
                  fullWidth
                  label={'Minimum Purchase Amount'}
                  name='minimumPurchaseAmount'
                  id='minimumPurchaseAmount'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.minimumPurchaseAmount}
                  error={Boolean(
                    errors.minimumPurchaseAmount &&
                      touched.minimumPurchaseAmount
                  )}
                  helperText={
                    Boolean(
                      errors.minimumPurchaseAmount &&
                        touched.minimumPurchaseAmount
                    ) && errors?.minimumPurchaseAmount
                  }
                />

                <TextField
                  fullWidth
                  label={'Maximum Discount Amount'}
                  name='maximumDiscountAmount'
                  id='maximumDiscountAmount'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.maximumDiscountAmount}
                  error={Boolean(
                    errors.maximumDiscountAmount &&
                      touched.maximumDiscountAmount
                  )}
                  helperText={
                    Boolean(
                      errors.maximumDiscountAmount &&
                        touched.maximumDiscountAmount
                    ) && errors?.maximumDiscountAmount
                  }
                />
              </Box>

              {/* <Box
                sx={{
                  py: 2,
                }}
              >
                <TextField
                  fullWidth
                  label={'Applies To'}
                  name='appliesTo'
                  id='appliesTo'
                  onChange={handleChange}
                  value={values.appliesTo}
                  error={Boolean(errors.appliesTo && touched.appliesTo)}
                  helperText={
                    Boolean(errors.appliesTo && touched.appliesTo) &&
                    errors?.appliesTo
                  }
                />
              </Box> */}

              <Box
                sx={{
                  py: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color='primary'
                      name='activeState'
                      onChange={(e) =>
                        setFieldValue('activeState', e.target.checked)
                      }
                      checked={values.activeState}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                    />
                  }
                  label={'Active Status'}
                />
              </Box>

              <Stack
                alignItems='center'
                sx={{ mt: 3 }}
                direction='row'
                justifyContent='flex-end'
                gap={1}
              >
                <Button
                  variant='outlined'
                  onClick={() => navigation('/promo-code')}
                >
                  {'Cancel'}
                </Button>

                <LoadingButton
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  {isAddMode ? 'Add Promo Code' : 'Edit Promo Code'}
                </LoadingButton>
              </Stack>
            </Card>
          </form>
        )}
      </Grid>
    </Grid>
  );
};

export default PromoCodeForm;
